import { defineStore } from 'pinia'
import { computed, onMounted, readonly, ref } from 'vue'
import { allActions } from './actionStore'

export type PrivilegeType = 'action' | 'hidden' | 'admin'
export type Privilege<T extends PrivilegeType = PrivilegeType> = T extends 'action'
  ? {
      type: 'action'
      action: string
    }
  : T extends 'hidden'
  ? {
      type: 'hidden'
    }
  : T extends 'admin'
  ? {
      type: 'admin'
    }
  : never

export const usePrivileges = defineStore('settings', () => {
  const privileges = ref<Privilege[]>([])
  const allowHidden = computed(() => privileges.value?.some((p) => p.type === 'hidden') || false)
  const allowWrite = computed(
    () => privileges.value?.some((p) => p.type === 'action' && p.action.startsWith('new')) || false
  )
  const isAdmin = computed(() => privileges.value?.some((p) => p.type === 'admin') || false)
  const allowedActions = computed(
    () =>
      privileges.value
        .filter((p) => p.type === 'action')
        .map((p) => (p as Privilege<'action'>).action) || []
  )

  function removeDuplicates<T>(array: T[]): T[] {
    return Array.from(new Set(array.map((item) => JSON.stringify(item)))).map((item) =>
      JSON.parse(item)
    )
  }

  function setPrivileges(newPrivileges: Privilege[]) {
    privileges.value = removeDuplicates(newPrivileges)
  }

  function showHidden() {
    privileges.value = [...(privileges.value || []), { type: 'hidden' }]
  }

  function hideHidden() {
    privileges.value = privileges.value?.filter((p) => p.type !== 'hidden') || []
  }

  function setAdmin() {
    privileges.value = [
      ...(privileges.value.filter((p) => p.type !== 'admin') || []),
      { type: 'admin' }
    ]
  }

  function unsetAdmin() {
    privileges.value = privileges.value?.filter((p) => p.type !== 'admin') || []
  }

  function setAllowedActions(actions: string[]) {
    setPrivileges([
      ...(privileges.value.filter((p) => p.type !== 'action') || []),
      ...actions.map((action) => ({
        type: 'action' as const,
        action
      }))
    ])
  }

  onMounted(() => {
    const roleStore = localStorage.getItem('roles')
    if (!roleStore) return
    const access = JSON.parse(roleStore)
    access.forEach((role: string) => {
      switch (role) {
        case 'FEAT_FILE_DELETE':
          break
        case 'FEAT_FILE_WRITE':
          break
        case 'FEAT_FILE_READ':
          break
        case 'FEAT_FILE_HIDDEN':
          break
        case 'ROLE_ADMIN':
          // Admins have all privileges
          privileges.value = allActions
            .map(
              (action) =>
                ({
                  type: 'action',
                  action: action.actionName
                } as Privilege)
            )
            .concat([{ type: 'admin' }])
          break
      }
    })
  })

  return {
    privileges: readonly(privileges),
    allowHidden,
    isAdmin,
    allowWrite,
    allowedActions,
    showHidden,
    hideHidden,
    setAdmin,
    unsetAdmin,
    setPrivileges,
    setAllowedActions
  }
})
